import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CreditCard, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { billing } from '../services/api/client';

/**
 * StripeCheckout Component
 * Handles subscription checkout via Stripe Checkout Session
 */
const StripeCheckout = ({
  plan,
  billingInterval = 'monthly',
  onSuccess,
  onCancel,
  buttonText = 'Souscrire',
  buttonClassName = ''
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCheckout = async () => {
    setLoading(true);
    setError(null);

    try {
      // Create checkout session via backend
      const response = await billing.createCheckoutSession(plan, billingInterval);

      if (response.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Une erreur est survenue lors du paiement');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        onClick={handleCheckout}
        disabled={loading}
        className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${buttonClassName}`}
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Redirection vers Stripe...</span>
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            <span>{buttonText}</span>
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
        <Lock className="w-3 h-3" />
        <span>Paiement securise par Stripe</span>
      </div>
    </div>
  );
};

/**
 * StripePaymentStatus Component
 * Shows payment success/cancel status after Stripe redirect
 */
export const StripePaymentStatus = ({ success, sessionId }) => {
  const navigate = useNavigate();

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
        <div className="bg-brand-black-light rounded-2xl p-8 border border-brand-green/50 max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-brand-green" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Paiement réussi !</h2>
          <p className="text-gray-400 mb-6">
            Votre abonnement a été activé avec succès.
            Vous pouvez maintenant profiter de toutes les fonctionnalités de votre plan.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 bg-brand-green text-black font-semibold rounded-xl hover:bg-brand-green/90 transition-all"
          >
            Accéder au Dashboard
          </button>
        </div>
      </div>
    );
  }

  return null;
};

/**
 * UpgradePlanModal Component
 * Modal for upgrading subscription plan
 */
export const UpgradePlanModal = ({ isOpen, onClose, currentPlan }) => {
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [billingInterval, setBillingInterval] = useState('yearly');

  const plans = [
    {
      id: 'pro',
      name: 'Pro',
      priceMonthly: 299,
      priceYearly: 2990,
      features: ['Scans IA illimites', '5 utilisateurs', '10 cibles', 'Support prioritaire']
    },
    {
      id: 'entreprise',
      name: 'Entreprise',
      priceMonthly: 899,
      priceYearly: 8990,
      features: ['Tout le plan Pro', '15 utilisateurs', '30 cibles', 'SSO/SAML', 'Support dédié']
    },
    {
      id: 'entreprise_plus',
      name: 'Groupe',
      priceMonthly: 1499,
      priceYearly: 14990,
      features: ['Tout le plan Entreprise', 'Utilisateurs illimites', '50 cibles', 'Support 24/7']
    }
  ];

  if (!isOpen) return null;

  const selectedPlanData = plans.find(p => p.id === selectedPlan);
  const price = billingInterval === 'yearly' ? selectedPlanData?.priceYearly : selectedPlanData?.priceMonthly;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
      <div className="bg-brand-black-light rounded-2xl border border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-2xl font-bold text-white">Changer de plan</h2>
          <p className="text-gray-400 mt-1">Sélectionnez le plan qui correspond à vos besoins</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Billing interval toggle */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 bg-brand-black rounded-xl p-1 border border-gray-800">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  billingInterval === 'monthly'
                    ? 'bg-brand-green text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  billingInterval === 'yearly'
                    ? 'bg-brand-green text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Annuel
                <span className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded-full font-bold">
                  -17%
                </span>
              </button>
            </div>
          </div>

          {/* Plan selection */}
          <div className="grid grid-cols-3 gap-4">
            {plans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                disabled={plan.id === currentPlan}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  selectedPlan === plan.id
                    ? 'border-brand-green bg-brand-green/10'
                    : plan.id === currentPlan
                    ? 'border-gray-700 bg-gray-800/50 opacity-50 cursor-not-allowed'
                    : 'border-gray-800 hover:border-gray-700'
                }`}
              >
                <h3 className="font-bold text-white">{plan.name}</h3>
                <div className="text-2xl font-bold text-brand-green mt-2">
                  {billingInterval === 'yearly' ? plan.priceYearly : plan.priceMonthly} EUR
                </div>
                <div className="text-xs text-gray-500">
                  /{billingInterval === 'yearly' ? 'an' : 'mois'} HT
                </div>
                {plan.id === currentPlan && (
                  <div className="text-xs text-gray-500 mt-2">Plan actuel</div>
                )}
              </button>
            ))}
          </div>

          {/* Selected plan features */}
          {selectedPlanData && (
            <div className="bg-brand-black rounded-xl p-4 border border-gray-800">
              <h4 className="font-semibold text-white mb-3">Inclus dans {selectedPlanData.name}:</h4>
              <ul className="space-y-2">
                {selectedPlanData.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-brand-green" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Checkout button */}
          <StripeCheckout
            plan={selectedPlan}
            billingInterval={billingInterval}
            buttonText={`Souscrire a ${selectedPlanData?.name} - ${price} EUR/${billingInterval === 'yearly' ? 'an' : 'mois'}`}
            buttonClassName="bg-brand-green hover:bg-brand-green/90 text-black"
          />
        </div>

        <div className="p-6 border-t border-gray-800">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 transition-all"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripeCheckout;
